import React from 'react';
import { CalendarDays, Calendar, Clock, Check, X, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatDate, formatNumber, pluralize } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета дат
 */
const ResultsDisplay = ({ results }) => {
  if (!results) return null;

  // Рендер результатов для разницы между датами
  const renderDifferenceResults = () => (
    <>
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общее количество дней */}
        <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 p-5 border border-cyan-100 dark:border-cyan-800/50 shadow-sm">
          <h3 className="text-base font-medium text-cyan-700 dark:text-cyan-300 mb-1.5 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4"/>
            {results.countWorkingDaysOnly ? 'Рабочих дней' : 'Всего дней'}
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-cyan-800 dark:text-cyan-200" aria-live="polite">
              {results.countWorkingDaysOnly ? results.workingDays : results.totalDays}
            </span>
            <span className="text-sm font-medium text-cyan-600 dark:text-cyan-400">
              {pluralize(results.countWorkingDaysOnly ? results.workingDays : results.totalDays, 'день', 'дня', 'дней')}
            </span>
          </div>
          <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-1">
            {results.includeEndDate && !results.countWorkingDaysOnly 
              ? 'Включая начальную и конечную дату'
              : results.countWorkingDaysOnly 
                ? 'Только рабочие дни (пн-пт)'
                : 'Разница между датами'}
          </p>
        </div>

        {/* Разбивка по периодам */}
        <div className="rounded-lg bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 p-5 border border-teal-100 dark:border-teal-800/50 shadow-sm">
          <h3 className="text-base font-medium text-teal-700 dark:text-teal-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            В других единицах
          </h3>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-teal-600 dark:text-teal-400">Недель:</span>
              <span className="text-sm font-medium text-teal-800 dark:text-teal-200">
                {results.difference.weeks} {results.difference.remainingDays > 0 && ` и ${results.difference.remainingDays} ${pluralize(results.difference.remainingDays, 'день', 'дня', 'дней')}`}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-teal-600 dark:text-teal-400">Месяцев:</span>
              <span className="text-sm font-medium text-teal-800 dark:text-teal-200">
                {results.difference.months} {results.difference.remainingMonths > 0 && results.difference.years > 0 && ` и ${results.difference.remainingMonths} ${pluralize(results.difference.remainingMonths, 'месяц', 'месяца', 'месяцев')}`}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-teal-600 dark:text-teal-400">Лет:</span>
              <span className="text-sm font-medium text-teal-800 dark:text-teal-200">
                {results.difference.years} {results.difference.remainingMonths > 0 && ` и ${results.difference.remainingMonths} ${pluralize(results.difference.remainingMonths, 'месяц', 'месяца', 'месяцев')}`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Начальная дата" 
          value={formatDate(results.startDate, 'long')} 
          color="cyan" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Конечная дата" 
          value={formatDate(results.endDate, 'long')} 
          color="cyan" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Всего календарных дней" 
          value={`${results.totalDays} ${pluralize(results.totalDays, 'день', 'дня', 'дней')}`} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Выходных дней (сб, вс)" 
          value={`${results.weekendDays} ${pluralize(results.weekendDays, 'день', 'дня', 'дней')}`} 
          color="yellow"
        />

        {results.holidaysCount > 0 && (
          <ResultItem 
            label="Праздничных дней" 
            value={`${results.holidaysCount} ${pluralize(results.holidaysCount, 'день', 'дня', 'дней')}`} 
            color="orange"
          />
        )}
        
        {results.countWorkingDaysOnly && (
          <ResultItem 
            label="Рабочих дней" 
            value={`${results.workingDays} ${pluralize(results.workingDays, 'день', 'дня', 'дней')}`} 
            color="green"
          />
        )}
        
        {results.specificDayCount > 0 && (
          <ResultItem 
            label={`Количество дней недели (${results.specificDayName})`}
            value={`${results.specificDayCount} ${pluralize(results.specificDayCount, 'день', 'дня', 'дней')}`} 
            color="purple"
          />
        )}
      </div>
    </>
  );

  // Рендер результатов для добавления дней
  const renderAddResults = () => (
    <>
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Результат добавления дней */}
        <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-emerald-50 dark:from-cyan-900/30 dark:to-emerald-900/30 p-5 border border-cyan-100 dark:border-cyan-800/50 shadow-sm">
          <h3 className="text-base font-medium text-cyan-700 dark:text-cyan-300 mb-1.5 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4"/>
            Результат
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-cyan-800 dark:text-cyan-200" aria-live="polite">
              {formatDate(results.resultDate, 'long')}
            </span>
          </div>
          <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-1">
            Дата после добавления {results.daysAdded} {pluralize(results.daysAdded, 'дня', 'дней', 'дней')}
          </p>
        </div>

        {/* Детали расчета */}
        <div className="rounded-lg bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/30 dark:to-emerald-900/30 p-5 border border-teal-100 dark:border-teal-800/50 shadow-sm">
          <h3 className="text-base font-medium text-teal-700 dark:text-teal-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            Детали расчета
          </h3>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-teal-600 dark:text-teal-400">Добавлено дней:</span>
              <span className="text-sm font-medium text-teal-800 dark:text-teal-200">
                {results.daysAdded} {pluralize(results.daysAdded, 'день', 'дня', 'дней')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-teal-600 dark:text-teal-400">Фактически прошло:</span>
              <span className="text-sm font-medium text-teal-800 dark:text-teal-200">
                {results.actualDaysElapsed} {pluralize(results.actualDaysElapsed, 'день', 'дня', 'дней')}
              </span>
            </div>
            {results.countWorkingDaysOnly && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-teal-600 dark:text-teal-400">Тип расчета:</span>
                <span className="text-sm font-medium text-teal-800 dark:text-teal-200">
                  Только рабочие дни
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Начальная дата" 
          value={formatDate(results.startDate, 'long')} 
          color="cyan" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Результат" 
          value={formatDate(results.resultDate, 'long')} 
          color="green" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Добавлено дней" 
          value={`${results.daysAdded} ${pluralize(results.daysAdded, 'день', 'дня', 'дней')}`} 
          icon={<CalendarDays className="w-4 h-4"/>}
        />
        
        {results.weekendDays > 0 && (
          <ResultItem 
            label="Выходных дней в периоде" 
            value={`${results.weekendDays} ${pluralize(results.weekendDays, 'день', 'дня', 'дней')}`} 
            color="yellow"
          />
        )}

        {results.holidaysCount > 0 && (
          <ResultItem 
            label="Праздничных дней в периоде" 
            value={`${results.holidaysCount} ${pluralize(results.holidaysCount, 'день', 'дня', 'дней')}`} 
            color="orange"
          />
        )}
        
        <ResultItem 
          label="Фактически прошло дней" 
          value={`${results.actualDaysElapsed} ${pluralize(results.actualDaysElapsed, 'день', 'дня', 'дней')}`} 
          color="blue"
        />
      </div>
    </>
  );

  // Рендер результатов для вычитания дней
  const renderSubtractResults = () => (
    <>
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Результат вычитания дней */}
        <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-indigo-50 dark:from-cyan-900/30 dark:to-indigo-900/30 p-5 border border-cyan-100 dark:border-cyan-800/50 shadow-sm">
          <h3 className="text-base font-medium text-cyan-700 dark:text-cyan-300 mb-1.5 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4"/>
            Результат
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-cyan-800 dark:text-cyan-200" aria-live="polite">
              {formatDate(results.resultDate, 'long')}
            </span>
          </div>
          <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-1">
            Дата после вычитания {results.daysSubtracted} {pluralize(results.daysSubtracted, 'дня', 'дней', 'дней')}
          </p>
        </div>

        {/* Детали расчета */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            Детали расчета
          </h3>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-indigo-600 dark:text-indigo-400">Вычтено дней:</span>
              <span className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
                {results.daysSubtracted} {pluralize(results.daysSubtracted, 'день', 'дня', 'дней')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-indigo-600 dark:text-indigo-400">Фактически прошло:</span>
              <span className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
                {results.actualDaysElapsed} {pluralize(results.actualDaysElapsed, 'день', 'дня', 'дней')}
              </span>
            </div>
            {results.countWorkingDaysOnly && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-indigo-600 dark:text-indigo-400">Тип расчета:</span>
                <span className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
                  Только рабочие дни
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Исходная дата" 
          value={formatDate(results.startDate, 'long')} 
          color="cyan" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Результат" 
          value={formatDate(results.resultDate, 'long')} 
          color="indigo" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Вычтено дней" 
          value={`${results.daysSubtracted} ${pluralize(results.daysSubtracted, 'день', 'дня', 'дней')}`} 
          icon={<CalendarDays className="w-4 h-4"/>}
        />
        
        {results.weekendDays > 0 && (
          <ResultItem 
            label="Выходных дней в периоде" 
            value={`${results.weekendDays} ${pluralize(results.weekendDays, 'день', 'дня', 'дней')}`} 
            color="yellow"
          />
        )}

        {results.holidaysCount > 0 && (
          <ResultItem 
            label="Праздничных дней в периоде" 
            value={`${results.holidaysCount} ${pluralize(results.holidaysCount, 'день', 'дня', 'дней')}`} 
            color="orange"
          />
        )}
        
        <ResultItem 
          label="Фактически прошло дней" 
          value={`${results.actualDaysElapsed} ${pluralize(results.actualDaysElapsed, 'день', 'дня', 'дней')}`} 
          color="blue"
        />
      </div>
    </>
  );

  return (
    <ToolResult title="Результаты расчета">
      {results.calculationType === 'difference' && renderDifferenceResults()}
      {results.calculationType === 'add' && renderAddResults()}
      {results.calculationType === 'subtract' && renderSubtractResults()}
      
      {/* Информация о расчете */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertCircle className="w-5 h-5 text-cyan-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Способ расчета</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {results.calculationType === 'difference'
                ? 'Расчет разницы между двумя датами с учетом всех указанных параметров'
                : results.calculationType === 'add'
                  ? 'Расчет даты после добавления указанного количества дней к начальной дате'
                  : 'Расчет даты после вычитания указанного количества дней из начальной даты'
              }
              <br />
              <span className="mt-1 inline-block">
                {results.countWorkingDaysOnly 
                  ? <span className="text-green-600 dark:text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> Учитываются только рабочие дни (пн-пт)</span>
                  : <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1"><X className="w-3 h-3" /> Учитываются все календарные дни</span>
                }
              </span>
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsDisplay;